import { Json } from "@visioni/a3s-codecs";
import z from "zod/v4";

/**
 * Canonical S3 user-metadata contract marking a stored clip as a test signal.
 *
 * Producer PutObject writes `x-amz-meta-a3s-test-signal: <JSON>`; the AWS SDK
 * strips the `x-amz-meta-` prefix and lower-cases the remainder on
 * HeadObject/GetObject, so the key read back is the bare value below. The
 * value is the JSON encoding of a {@link TestSignalContext}. Producers
 * (`store*`) and the intake reader both import these so the write and read
 * sides cannot drift.
 */
export const TEST_SIGNAL_METADATA_KEY = "a3s-test-signal";

/** What produced a test signal: an operator's manual probe, or the automatic periodic heartbeat. */
export const TestSignalTriggerTypeSchema = z.enum(["manual", "periodic"]);
export type TestSignalTriggerType = z.infer<typeof TestSignalTriggerTypeSchema>;

/**
 * Which delivery path carried a test signal's clip to a3s.
 *
 * - `camera` — the camera uploaded the clip itself, straight to a3s (to the
 *   cloud, or via the edge upload proxy on S15).
 * - `va` — the VA container produced the clip and posted it to what it believes
 *   is Patriot; a3s intercepts on `:5090` and re-uploads it to `/va/upload`.
 *
 * Distinct from {@link TestSignalTriggerTypeSchema}, which names the *mechanism*
 * that fired the probe (an operator, or the automatic heartbeat) rather than the
 * route its clip travelled. A Mobotix-VA camera emits both paths, and they cover
 * different legs — so "this asset went quiet" is only actionable if you can tell
 * which one stopped arriving.
 *
 * Deliberately not called `route`: cloud-side "routing" already means resolving
 * which monitoring provider a signal is dispatched to.
 */
export const TestSignalPathSchema = z.enum(["camera", "va"]);
export type TestSignalPath = z.infer<typeof TestSignalPathSchema>;

/**
 * Structured test-signal contract shared end-to-end: the query param, the S3
 * metadata value, and the producer-side options are all this same shape.
 * `triggerType` and `signalPath` are only present (and only meaningful) when
 * `isTestSignal` is `true` — a plain object rather than a bare boolean so future
 * test-signal annotations have one place to grow instead of another parallel
 * flag.
 *
 * `signalPath` defaults rather than being required, and the default is
 * load-bearing: clips stored before this property shipped carry metadata with no
 * `signalPath`, and
 * {@link readTestSignalMetadata} fails open to `{isTestSignal: false}` on a
 * decode failure. A required field would therefore turn every in-flight test
 * signal into an apparent *real alarm* for the duration of a rollout. `camera`
 * is the correct default because it was the only path that existed before.
 */
export const TestSignalContextSchema = z.discriminatedUnion("isTestSignal", [
  z.object({ isTestSignal: z.literal(false) }),
  z.object({
    isTestSignal: z.literal(true),
    triggerType: TestSignalTriggerTypeSchema,
    signalPath: TestSignalPathSchema.default("camera"),
  }),
]);
export type TestSignalContext = z.infer<typeof TestSignalContextSchema>;

/**
 * Producer-side upload annotations threaded into every `store*` producer as a
 * required trailing options object, not a bare positional boolean.
 * `isTestSignal` names the field to match intake/`accept()`'s naming of the
 * same concept. Same shape as {@link TestSignalContext}, kept as a distinct
 * alias for its role at the producer call site.
 */
export type VideoUploadOptions = TestSignalContext;

/** Shared by the query param and the S3 metadata value, so their encodings can never drift apart. */
export const TestSignalContextJson = Json(TestSignalContextSchema);

/**
 * Shared by every reader of the flag — the intake reader, the two
 * clip-suppression Lambdas, and the Mobotix MXG→MP4 converter (which copies
 * it onto the MP4) — so the decode lives in one place. Fail-open: a missing
 * key or a malformed/invalid value decodes to `{isTestSignal: false}`.
 */
export const readTestSignalMetadata = (metadata: Record<string, string> | undefined): TestSignalContext => {
  const raw = metadata?.[TEST_SIGNAL_METADATA_KEY];
  if (raw === undefined) return { isTestSignal: false };
  const result = TestSignalContextJson.safeDecode(raw);
  return result.success ? result.data : { isTestSignal: false };
};
